import axios from 'axios'
import useSWR from 'swr'

const fetcher = (url: string) => axios.get(url).then((res) => res.data)

const readCookiePreferences = (): UpdatedPreferences | undefined => {
  try {
    const match = document.cookie
      .split('; ')
      .find((row) => row.startsWith('_laced_cookie_preferences='))
    if (!match) return undefined

    const cookie = JSON.parse(decodeURIComponent(match.split('=').slice(1).join('=')))
    if (!cookie.expires) return undefined

    // For logged-in users the DB is the source of truth, not the cookie.
    // The API checks consent validity against DB timestamps which may differ.
    if (cookie.has_persisted_to_db) return undefined

    const isValid = new Date(cookie.expires) > new Date()
    if (!isValid) return undefined

    return {
      valid: true,
      preferences: {
        advertising: cookie.advertising,
        analytical: cookie.analytical,
        essential: true,
        functional: cookie.functional
      }
    }
  } catch {
    return undefined
  }
}

export const useCookieConsentDecision = () => {
  const cachedPreferences = readCookiePreferences()

  const {
    data: existingCookiePreferences,
    error,
    mutate
  } = useSWR('/api/consent_decisions/me/cookies', fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false,
    revalidateOnMount: !cachedPreferences,
    // we dont want it to fail, show the popup, then suddenly hide popup again
    shouldRetryOnError: false,
    fallbackData: cachedPreferences
  })

  const updateCookieConsentDecision = async (
    userId: number,
    preferences: CookiePreferences
  ) => {
    let updatedPreferences: UpdatedPreferences = {
      valid: false,
      preferences: {
        advertising: false,
        analytical: false,
        essential: false,
        functional: false
      }
    }

    await mutate(async () => {
      try {
        const res = await axios.post('/api/consent_decisions/me/cookies', {
          user_id: userId,
          source: 'cookie_preferences_popup',
          preferences
        })

        updatedPreferences = res.data

        return updatedPreferences
      } catch {
        return { preferences, valid: true }
      }
    })

    return updatedPreferences
  }

  return {
    loading: existingCookiePreferences === undefined && !error,
    existingCookiePreferences,
    error,
    updateCookieConsentDecision,
    mutate
  }
}

type CookiePreferences = {
  advertising: boolean
  analytical: boolean
  essential: boolean
  functional: boolean
}

type UpdatedPreferences = {
  valid: boolean
  preferences: CookiePreferences
}
